/* @sravni/react-design-system version 0.2.60 */
import { __rest, __read, __assign } from 'tslib';
import * as React from 'react';
import { useStyles, useMergedRef } from '@sravni/react-utils';
import SheetContext from './SheetContext.js';
import modules_0b64ef5e from '../styles/Sheet.module.less.js';

/**
 * @description Sheet Content component.
 *
 * @component
 * @example
 * ```jsx
 * <Sheet.Content>content</Sheet>
 * ```
 */
var Content = React.forwardRef(function (props, ref) {
    var _a;
    var className = props.className, children = props.children, restProps = __rest(props, ["className", "children"]);
    var setDragElastic = React.useContext(SheetContext).setDragElastic;
    var containerRef = React.useRef(null);
    var allowUp = React.useRef(false);
    var allowDown = React.useRef(false);
    var prevPageY = React.useRef(0);
    var prevPageX = React.useRef(0);
    var _b = __read(React.useState(false), 2), dividerVisible = _b[0], setDividerVisible = _b[1];
    var cx = useStyles(modules_0b64ef5e);
    var classNames = cx(className, 'content', (_a = {},
        _a['divider'] = dividerVisible,
        _a));
    var updateDivider = React.useCallback(function () {
        var container = containerRef === null || containerRef === void 0 ? void 0 : containerRef.current;
        if (!container)
            return;
        setDividerVisible(container.scrollHeight > container.clientHeight);
    }, [containerRef]);
    var handleTouchStart = React.useCallback(function (event) {
        var container = containerRef.current;
        if (!container)
            return;
        allowUp.current = container.scrollTop > 0;
        allowDown.current = container.scrollTop < container.scrollHeight - container.clientHeight;
        prevPageY.current = event.touches[0].pageY;
        prevPageX.current = event.touches[0].pageX;
        if (setDragElastic) {
            allowUp.current && !allowDown.current && setDragElastic({ top: 0.5, bottom: 0 });
            !allowUp.current && allowDown.current && setDragElastic({ top: 0, bottom: 0.5 });
            allowUp.current && allowDown.current && setDragElastic({ top: 0, bottom: 0 });
        }
    }, [containerRef]);
    var handleTouchMove = React.useCallback(function (event) {
        var moveUp = event.touches[0].pageY > prevPageY.current;
        var moveDown = event.touches[0].pageY < prevPageY.current;
        var horizontalMove = event.touches[0].pageX > prevPageX.current || event.touches[0].pageX < prevPageX.current;
        prevPageY.current = event.touches[0].pageY;
        prevPageX.current = event.touches[0].pageX;
        if ((moveUp && allowUp.current) || (moveDown && allowDown.current)) {
            event.stopPropagation();
            return;
        }
        if (horizontalMove && !moveUp && !moveDown)
            return;
        event.preventDefault();
    }, [prevPageY, prevPageX, allowUp, allowDown]);
    var stopPropagationPointerDown = React.useCallback(function (event) { return event.stopPropagation(); }, []);
    React.useEffect(function () {
        updateDivider();
        window.addEventListener('resize', updateDivider);
        return function () { return window.removeEventListener('resize', updateDivider); };
    }, [updateDivider]);
    React.useEffect(function () {
        var container = containerRef.current;
        container === null || container === void 0 ? void 0 : container.addEventListener('touchstart', handleTouchStart);
        container === null || container === void 0 ? void 0 : container.addEventListener('touchmove', handleTouchMove);
        return function () {
            container === null || container === void 0 ? void 0 : container.removeEventListener('touchstart', handleTouchStart);
            container === null || container === void 0 ? void 0 : container.removeEventListener('touchmove', handleTouchMove);
        };
    }, [handleTouchStart, handleTouchMove]);
    return (React.createElement("div", __assign({}, restProps, { ref: useMergedRef(ref, containerRef), className: classNames, onPointerDown: stopPropagationPointerDown }), children));
});
Content.defaultProps = {};
Content.displayName = 'Content';

export default Content;
//# sourceMappingURL=Content.js.map
